import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import InfoTooltip from 'components/InfoTooltip';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Input } from 'components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Control, Controller, useWatch } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { describeDerivedTargets, formatHours, MAX_PLANNED_WORKDAYS_OFF, NO_VALUE } from './progressCalculation';
import { useProgressMetric } from './progressMetric';

export type TargetsForm = {
    annualTargetHours?: number | null;
    plannedWorkdaysOff?: number | null;
};

type TargetField = keyof TargetsForm;

type Props = {
    control: Control<TargetsForm>;
    readonly?: boolean;
    /**
     * The saved days off, accepted as is even above the cap: an earlier cap allowed 260, and the employee form resends
     * it on every save. The targets modal passes none, since its endpoint keeps the strict cap.
     */
    storedPlannedWorkdaysOff?: number | null;
};

type IntegerFieldProps = {
    control: Control<TargetsForm>;
    name: TargetField;
    /** Already translated. */
    label: string;
    placeholder: string;
    unit: string;
    readonly: boolean;
    /** Marks the field required and shows a blank one as an error from the start, before any validation. */
    required?: boolean;
    /**
     * Checks the value, `null` when blank. Reads the other field from `values`, not from a closure: a field it depends
     * on re-validates this one before the component re-renders.
     */
    validate: (value: number | null, values: TargetsForm) => true | string;
    format: (value: number) => string;
    tooltip?: JSX.Element;
    /** The field to re-validate whenever this one is, since its rule reads this one. */
    deps?: TargetField;
};

const IntegerField = ({
    control,
    name,
    label,
    placeholder,
    unit,
    readonly,
    required = false,
    validate,
    format,
    tooltip,
    deps,
}: IntegerFieldProps) => {
    const id = `field_${name}`;
    return (
        <Controller
            control={control}
            name={name}
            // A read-only value is not checked: the caller cannot change it, and the API ignores it for them.
            rules={{ deps, validate: (value, values) => readonly || validate(value ?? null, values) }}
            render={({ field, fieldState: { error } }) => {
                const isEmpty = field.value === null || field.value === undefined;
                // A blank value can only fail as required, so its error follows `required` rather than the last run.
                const blankError = required ? { message: t('validation.required-field') } : undefined;
                const shownError = readonly ? undefined : isEmpty ? blankError : error;
                return (
                    <InputWrapper
                        name={id}
                        label={label}
                        shouldTranslateLabel={false}
                        tooltip={tooltip}
                        required={required}
                        isEmpty={isEmpty}
                        readonly={readonly}
                        hasErrors={!!shownError}
                        error={shownError}
                    >
                        {readonly ? (
                            <ReadonlyField
                                value={
                                    field.value === null || field.value === undefined ? NO_VALUE : format(field.value)
                                }
                            />
                        ) : (
                            <InputGroup className="bg-paper">
                                <NumberFormat
                                    customInput={InputGroupInput}
                                    id={id}
                                    value={field.value ?? ''}
                                    thousandSeparator={name === 'annualTargetHours' ? ',' : false}
                                    decimalScale={0}
                                    allowNegative={false}
                                    placeholder={placeholder}
                                    aria-invalid={!!shownError}
                                    onValueChange={({ floatValue }) => field.onChange(floatValue ?? null)}
                                    onBlur={field.onBlur}
                                    getInputRef={field.ref}
                                />
                                <InputGroupAddon align="inline-end">{unit}</InputGroupAddon>
                            </InputGroup>
                        )}
                    </InputWrapper>
                );
            }}
        />
    );
};

const DerivedField = ({
    name,
    label,
    value,
    readonly,
}: {
    name: string;
    label: string;
    value: string;
    readonly: boolean;
}) => (
    <InputWrapper name={`field_${name}`} label={label} hasErrors={false} readonly={readonly}>
        {readonly ? (
            <ReadonlyField value={value} />
        ) : (
            <Input id={`field_${name}`} readOnly tabIndex={-1} value={value} className="bg-muted" />
        )}
    </InputWrapper>
);

/** The two editable targets and the two values derived from them, shared by the targets modal and the employee form. */
const TargetsFields = ({ control, readonly = false, storedPlannedWorkdaysOff }: Props) => {
    const { displayDecimalScale } = useTimecardPrecision();
    const metric = useProgressMetric();
    const [annualTargetHours, plannedWorkdaysOff] = useWatch({
        control,
        name: ['annualTargetHours', 'plannedWorkdaysOff'],
    });
    const derived = describeDerivedTargets(
        annualTargetHours,
        plannedWorkdaysOff,
        displayDecimalScale,
        new Date().getFullYear()
    );
    // On a record page each target is a cell of the card's grid, where the read view has its row.
    const inSection = useInRecordSection();

    return (
        <div className={inSection ? 'contents' : 'flex flex-col gap-4'}>
            <IntegerField
                control={control}
                name="annualTargetHours"
                label={t('dashboard.progress-tracking.annual-target-with-metric', { metric: t(metric.phraseId) })}
                placeholder={t('dashboard.progress-tracking.annual-target-placeholder')}
                unit={t('dashboard.progress-tracking.hours-unit')}
                readonly={readonly}
                validate={(value) =>
                    value === null || value >= 1 || t('dashboard.progress-tracking.validation.annual-target')
                }
                format={(value) => formatHours(value, 0)}
                tooltip={<InfoTooltip text={metric.tooltipId} />}
                deps="plannedWorkdaysOff"
            />
            <IntegerField
                control={control}
                name="plannedWorkdaysOff"
                label={t('dashboard.progress-tracking.planned-workdays-off')}
                placeholder={t('dashboard.progress-tracking.planned-workdays-off-placeholder')}
                unit={t('dashboard.progress-tracking.days-unit')}
                readonly={readonly}
                required={!readonly && typeof annualTargetHours === 'number'}
                validate={(value, values) => {
                    if (value === null) {
                        return typeof values.annualTargetHours !== 'number' || t('validation.required-field');
                    }
                    return (
                        value <= MAX_PLANNED_WORKDAYS_OFF ||
                        value === storedPlannedWorkdaysOff ||
                        t('dashboard.progress-tracking.validation.planned-workdays-off', {
                            max: String(MAX_PLANNED_WORKDAYS_OFF),
                        })
                    );
                }}
                format={String}
                tooltip={<InfoTooltip text="dashboard.progress-tracking.planned-workdays-off-tooltip" />}
            />
            <DerivedField
                name="plannedWorkdaysPerYear"
                label="dashboard.progress-tracking.planned-workdays-per-year"
                value={derived.plannedWorkdaysPerYear}
                readonly={readonly}
            />
            <DerivedField
                name="dailyTarget"
                label="dashboard.progress-tracking.daily-target-per-working-day"
                value={derived.dailyTarget}
                readonly={readonly}
            />
        </div>
    );
};

export default TargetsFields;
